/* eslint-disable max-lines */
import { ChevronLeft, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { track } from "common/utils/mixpanel/tracker";
import { sendDataLayer } from "common/utils/ga";
import { useFlickity } from "./useFlickity";
import { useCompositionPreviews } from "./useCompositionPreviews";
import type { SavedProject } from "./types";
import "flickity/css/flickity.css";

type SavedProjectCardProps = {
  project: SavedProject;
  onEditClick: (project: SavedProject) => void;
  onDelete?: (projectId: string) => Promise<void>;
  isDeleting?: boolean;
};

function SavedProjectCardComponent({
  project,
  onEditClick,
  onDelete,
  isDeleting = false
}: SavedProjectCardProps) {
  const { isDesktop } = useResponsive();
  const { previews, isGeneratingPreviews } = useCompositionPreviews(project.compositions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const hasMultipleCompositions = project.compositions.length > 1;

  const { carouselRef, flickity, next, previous } = useFlickity({
    options: {
      cellAlign: "center",
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: hasMultipleCompositions
    },
    enabled: hasMultipleCompositions
  });

  useEffect(() => {
    if (!flickity) return undefined;

    const handleChange = (index: unknown) => {
      setCurrentIndex(index as number);
    };

    flickity.on("change", handleChange);

    return () => {
      flickity.off("change", handleChange);
    };
  }, [flickity]);

  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!onDelete) return;

      // eslint-disable-next-line no-alert, no-restricted-globals
      const confirmed = window.confirm(
        `Are you sure you want to delete "${project.name}"? This action cannot be undone.`
      );

      if (confirmed) {
        await onDelete(project.uuid);
      }
    },
    [onDelete, project.uuid, project.name]
  );

  const renderCompositionImage = useCallback(
    (compositionIndex: number) => {
      const compositionPreviews = previews[compositionIndex];

      if (isGeneratingPreviews) {
        return (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="size-8 animate-spin text-gray-400" />
          </div>
        );
      }

      const frontUrl = compositionPreviews?.front;
      const backUrl = compositionPreviews?.back;
      const hasBothSides = frontUrl && backUrl;

      if (!frontUrl && !backUrl) return null;

      const primaryUrl = frontUrl || backUrl;
      const secondaryUrl = hasBothSides ? backUrl : null;

      return (
        <div className="relative flex h-full w-full items-center justify-center">
          <img
            src={primaryUrl}
            alt="Design preview"
            className="max-h-full max-w-full object-contain"
            loading="lazy"
          />
          {secondaryUrl && (
            <div className="bg-background absolute bottom-0 right-0 m-1.5 h-16 w-16 rounded-sm border border-gray-200 lg:h-20 lg:w-20">
              <img
                src={secondaryUrl}
                alt="Back design preview"
                className="size-full object-contain p-1"
                loading="lazy"
              />
            </div>
          )}
        </div>
      );
    },
    [previews, isGeneratingPreviews]
  );

  const handleEditClick = useCallback(() => {
    if (!isDeleting) {
      onEditClick(project);
    }
  }, [onEditClick, project, isDeleting]);

  const handleAddToCartClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isAddingToCart) {
        e.preventDefault();
        return;
      }
      setIsAddingToCart(true);

      track("custom_saved_project_add_to_cart_clicked", {
        eventCategory: "productclick",
        page_location: "design_lab_saved_projects",
        business_line: "print",
        section_identifier: "saved_projects",
        project_uuid: project.uuid,
        project_name: project.name
      });

      sendDataLayer({
        genericEvent: true,
        eventCategory: "productclick",
        event: "click",
        page_location: "design_lab_saved_projects",
        business_line: "print",
        section_identifier: "saved_projects"
      });
    },
    [isAddingToCart, project.uuid, project.name]
  );

  return (
    <div
      className={`font-inter bg-background relative flex w-full flex-col overflow-hidden rounded-lg transition-all lg:max-w-full xl:max-w-56 ${
        isDeleting ? "pointer-events-none opacity-50" : "hover:shadow-md"
      }`}
    >
      {isDeleting && (
        <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="size-8 animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-700">Deleting...</p>
          </div>
        </div>
      )}

      <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-gray-200">
        {hasMultipleCompositions ? (
          <>
            <div
              ref={carouselRef}
              className="size-full [&_.flickity-slider]:h-full [&_.flickity-viewport]:h-full"
            >
              {project.compositions.map((_, index) => (
                <div key={index} className="size-full">
                  {renderCompositionImage(index)}
                </div>
              ))}
            </div>

            {currentIndex > 0 && !isDeleting && (
              <button
                onClick={previous}
                className="bg-background/90 hover:bg-background absolute left-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-2 shadow-md transition-all hover:shadow-lg"
                aria-label="Previous image"
                data-testid="carousel-previous-button"
                type="button"
              >
                <ChevronLeft className="size-5 text-gray-700" />
              </button>
            )}

            {currentIndex < project.compositions.length - 1 && !isDeleting && (
              <button
                onClick={next}
                className="bg-background/90 hover:bg-background absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-2 shadow-md transition-all hover:shadow-lg"
                aria-label="Next image"
                data-testid="carousel-next-button"
                type="button"
              >
                <ChevronRight className="size-5 text-gray-700" />
              </button>
            )}
          </>
        ) : (
          <div className="size-full">{renderCompositionImage(0)}</div>
        )}

        {onDelete && !isDeleting && (
          <button
            onClick={handleDelete}
            className="bg-background/90 hover:bg-background absolute right-2 top-2 cursor-pointer rounded-[4px] border border-gray-400 p-0.5 shadow-md transition-all hover:shadow-lg"
            aria-label="Delete project"
            data-testid={`delete-project-${project.uuid}`}
            type="button"
          >
            <Trash2 className="size-6 text-gray-500" />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-2">
        <div>
          <h3 className="truncate text-lg font-semibold text-gray-900">{project.name}</h3>
          <p className="text-xs text-gray-500">Saved {project.lastUpdatedAt}</p>
        </div>

        <div className="flex flex-col gap-2">
          {project.addToCartUrl && project.addToCartUrl.trim() !== "" && (
            <a
              data-testid={`add-to-cart-${project.uuid}`}
              href={project.addToCartUrl}
              data-disable-with="Adding..."
              onClick={handleAddToCartClick}
              className={`flex h-10 w-full items-center justify-center rounded-lg border-2 border-blue-600 bg-blue-600 py-2.5 text-base font-semibold text-white no-underline transition-colors lg:h-11 ${
                isAddingToCart
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-white hover:text-blue-600"
              }`}
            >
              {isAddingToCart ? "Adding..." : "Add to Cart"}
            </a>
          )}

          <Button
            data-testid={`edit-design-${project.uuid}`}
            variant="outline"
            className="h-10 w-full rounded-lg border-blue-600 py-2.5 text-base font-semibold text-blue-600 hover:bg-gray-100 hover:text-blue-700 lg:h-11"
            onPress={handleEditClick}
            isDisabled={isDeleting}
          >
            {isDesktop ? "Edit Design & Products" : "Edit Design"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export const SavedProjectCard = memo(SavedProjectCardComponent);
